import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import Grid from '@mui/material/Grid'
import useInterval from 'use-interval'
import { Dispatch, bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import _toNumber from 'lodash/toNumber'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import { useBreakpoint } from 'utils/Hooks'
import { RootActionType, RootState } from 'duck'
import Button from 'components/Button'
import CardCollection from 'components/Card/CardCollection'
import Typography from 'components/Typography'
import { actions, selectors } from '../duck'
import { apiActions } from 'duck/ApiDuck/actions'
import { apiSelectors } from 'duck/ApiDuck'
import TextFieldNumber from '../components/TextFieldNumber'
import { dialogActions, DialogNames } from 'duck/DialogDuck'
import Tooltip from 'components/Tooltip'
import LabelInfo from '../components/LabelInfo'
import LabelInfoPrice from '../components/LabelInfo/LabelInfoPrice'
import { route } from 'routes'
import stylesGlobal from 'stylesGlobal.module.scss'
import styles from './ProjectMine.module.scss'

const RETRIEVE_MINE_IMAGE_INTERVAL = 2000

const Utils = {
  setFormattedValue: (value: string) => {
    const newValue = value.padStart(8, '0')

    return newValue
  }
}

const mapStateToProps = (state: RootState) => ({
  currentMineImage: selectors.currentMineImage(state),
  openedMineProject: apiSelectors.openedMineProject(state),
  currentUserAddress: apiSelectors.currentUserAddress(state),
  currentSeed: selectors.currentSeed(state),
  generateError: selectors.generateError(state),
  createMineImageLoading: apiSelectors.loading['mineProject.createMineImage'](state)
})

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      generateMineImage: actions.generateMineImage,
      setError: actions.setError,
      addDialog: dialogActions.addDialog,
      pickSeedToGenerate: actions.pickSeedToGenerate,
      retrieveMineImage: apiActions.mineProject.retrieveMineImage
    },
    dispatch
  )

type ProjectMineProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>

const ProjectMine: React.FC<ProjectMineProps> = props => {
  const {
    generateMineImage,
    retrieveMineImage,
    pickSeedToGenerate,
    setError,
    addDialog,
    openedMineProject,
    currentUserAddress,
    generateError,
    createMineImageLoading,
    currentMineImage,
    currentSeed
  } = props

  const { isSmallscreenAbove } = useBreakpoint()
  const isError = false
  const [showValuePlaceholder, setShowValuePlaceholder] = useState(true)
  const [value, setValue] = useState(Utils.setFormattedValue('0'))
  const disableGenerate = !_toNumber(value)
  const shouldPooling = currentMineImage?.status === 'UNKNOWN'
  const disableMint =
    !currentMineImage || (!currentMineImage?.image && currentMineImage?.status === 'UNKNOWN')

  useEffect(() => {
    /* Detect currentSeed value change */
    setValue(Utils.setFormattedValue((currentSeed ?? '').toString()))
  }, [currentSeed])

  useInterval(
    () => {
      retrieveMineImage(currentMineImage?.id ?? 0)
    },
    shouldPooling ? RETRIEVE_MINE_IMAGE_INTERVAL : false,
    false
  )

  const [DUMMYisAutolevelActive, DUMMYsetAutolevelActive] = useState(false)

  return (
    <div>
      <Button
        startIcon={<KeyboardArrowLeftIcon />}
        className={clsx(
          stylesGlobal.MarginBottom24,
          stylesGlobal['-MarginLeft16'],
          stylesGlobal['-MarginTop10']
        )}
        to={route.MINE_PROJECTS.getUrl({
          id: openedMineProject?.id ?? 0,
          subRoute: 'summary'
        })}
      >
        Back
      </Button>

      <Typography
        variant="body1"
        fontWeight="bold"
        align="center"
        className={stylesGlobal.MarginBottom24}
      >
        Enter any 8 digit number to generate an output.
      </Typography>

      <div
        className={clsx(
          stylesGlobal.PositionRelative,
          isSmallscreenAbove ? stylesGlobal.MarginBottom78 : stylesGlobal.MarginBottom24
        )}
      >
        <div className={clsx(stylesGlobal.MarginXAuto, styles.PageMineContentWrapper)}>
          <div
            className={clsx(
              stylesGlobal.PositionRelative,
              stylesGlobal.MarginBottom16,
              styles.PageMineTextFieldWrapper
            )}
          >
            <TextFieldNumber
              value={value}
              placeholder="Enter Number"
              className={styles.PageMineTextField}
              color={isError ? 'error' : undefined}
              focused={isError}
              size="small"
              InputProps={
                showValuePlaceholder
                  ? {
                      classes: {
                        input: styles.PageMineTextFieldInput
                      }
                    }
                  : undefined
              }
              onChange={(value: string) => {
                setValue(value)
                setError({ errorString: undefined })
              }}
              showPlaceholder={showValuePlaceholder}
              fullWidth
            />
          </div>

          <Grid spacing={2} justifyContent="center" container>
            <Grid xs={6} sm="auto" item>
              <Button
                variant="contained"
                disabled={Boolean(disableGenerate || createMineImageLoading)}
                color="secondary"
                onClick={() => {
                  generateMineImage({
                    seed: _toNumber(value)
                  })

                  setShowValuePlaceholder(false)
                }}
                fullWidth
              >
                Generate
              </Button>
            </Grid>

            <Grid xs={6} sm="auto" item>
              <Button
                variant="contained"
                color="secondary"
                disabled={Boolean(createMineImageLoading)}
                onClick={() => {
                  pickSeedToGenerate()
                }}
                fullWidth
              >
                Pick For Me
              </Button>
            </Grid>
          </Grid>
        </div>

        {generateError ? (
          <Typography
            variant="body1"
            align="center"
            component="div"
            className={clsx(
              stylesGlobal.PositionAbsolute,
              stylesGlobal.MarginTop16,
              stylesGlobal.WidthFull,
              styles.PageMineInputHelper
            )}
            color={generateError ? 'error' : undefined}
          >
            {generateError}
          </Typography>
        ) : null}
      </div>

      <div className={clsx(stylesGlobal.MarginXAuto, styles.PageMineContentWrapper)}>
        <CardCollection
          network={openedMineProject?.network ?? 'ETHEREUM'}
          mineImage={currentMineImage}
          priceTooltip="Prices are subject to change. Click Mint Now to see the updated price."
          titleVariant="caption"
          loading={DUMMYisAutolevelActive}
          imageOnly={!currentMineImage}
          showPrice
        />

        {/* BACKEND STILL NOT READY YET */}
        {/* <div className={stylesGlobal.MarginTop24}>
          <Typography
            variant="caption"
            component="div"
            color="light"
            className={stylesGlobal.MarginBottom12}
          >
            Image Adjustments
          </Typography>

          <div className={clsx(stylesGlobal.DisplayFlex, stylesGlobal.AlignItemsCenter)}>
            <Button
              variant={'outlined'}
              color={DUMMYisAutolevelActive ? 'secondary' : undefined}
              onClick={() => {
                DUMMYsetAutolevelActive(prev => !prev)
              }}
              disabled={disableMint}
            >
              Auto Adjust
            </Button>

            <Tooltip
              className={clsx(
                stylesGlobal.HeightFull,
                stylesGlobal.MarginLeft8,
                stylesGlobal.TextLeft
              )}
              title="Automatically adjusts the blacks and whites of your image to make it look more balanced"
              placement={isSmallscreenAbove ? 'right-end' : 'bottom'}
            >
              <HelpOutlineIcon
                className={clsx(stylesGlobal.TextColorLight, stylesGlobal.FontSize16)}
              />
            </Tooltip>
          </div>
        </div> */}

        <div className={stylesGlobal.MarginY48}>
          <Grid spacing={4} container>
            <Grid xs={12} item>
              <LabelInfoPrice mineProject={openedMineProject} />
            </Grid>

            {openedMineProject?.mine_limit && openedMineProject?.mine_count ? (
              <Grid xs={6} item>
                <LabelInfo
                  className={stylesGlobal.MarginBottom16}
                  title="Remaining"
                  content={openedMineProject?.mine_limit - openedMineProject?.mine_count}
                />
              </Grid>
            ) : null}

            {openedMineProject?.mine_limit ? (
              <Grid xs={6} item>
                <LabelInfo
                  className={stylesGlobal.MarginBottom16}
                  title="Total in Collection"
                  content={openedMineProject?.mine_limit}
                />
              </Grid>
            ) : null}

            <Grid xs={6} item>
              <LabelInfo
                className={stylesGlobal.MarginBottom16}
                title="License"
                content={openedMineProject?.license || '-'}
              />
            </Grid>
          </Grid>
        </div>

        <Button
          variant="contained"
          color="secondary"
          disabled={disableMint}
          onClick={() => {
            currentUserAddress
              ? addDialog({
                  [DialogNames.MINT_DIALOG_COLLECTOR_CURATED]: {
                    dialogName: DialogNames.MINT_DIALOG_COLLECTOR_CURATED,
                    mineImageId: currentMineImage?.id || 0
                  }
                })
              : addDialog({
                  [DialogNames.CONNECT_TO_METAMASK]: {
                    dialogName: DialogNames.CONNECT_TO_METAMASK,
                    actionText: 'mint the image'
                  }
                })
          }}
          fullWidth
        >
          Mint Now
        </Button>
      </div>
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectMine)
